/**
 * Main function that handles the file import and analysis process.
 * 
 * This function includes:
 * - Tabs for selecting between simple consolidated list and consolidated list with statuses.
 * - Stepper to guide the user through the import and analysis steps.
 * - Help section with instructions for the user.
 * - File upload and reset functionality.
 * - Error handling and display.
 * - Conditional rendering based on the current step and tab selection.
 * 
 * @component
 * @returns {JSX.Element} The rendered component.
 */

import * as React from 'react';
import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import SelectList from '../Referentiel/SelectRef';
import Switch from '@mui/material/Switch';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import Importer from '../Import/Importer';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton'
import Icon from '@mui/material/Icon';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UploadFile from '../Import/UploadFile';
import UnitSelection from '../TerrUnit/SelectTerrUnit';
import MatchingTable from '../Taxon/MatchingTable';
import StatusTable from '../Status/StatusTable';
import SearchIcon from '@mui/icons-material/Search';
import { TabIndex, CustomTabPanel } from '@front-ends/common/src/components/ui/Tabs';
import { StoreFile, StoreRef, StoreStatus } from '../../hooks/store';
import { FileArray } from '../../models/types';
import { useTranslation } from 'react-i18next';

export default function Main() {

	const { t } = useTranslation();

	const steps = [t("import-title"), t("analyze-title")];
	const stepsStatus = [t("import-title"), t("analyze-title"), t("statutes-title")];

	const [tab, SetTab] = React.useState(0);
	const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
		SetTab(newValue);
	};

	const [file, setFile] = React.useState<File>();
	const [referentiel, setReferentiel] = React.useState('');
	const [refVersion, setRefVersion] = React.useState('');
	const [filterType, setFilterType] = React.useState('');
	const [terrUnit, setTerrUnit] = React.useState('');
	const [fileData, setfileData] = React.useState<FileArray[]>([]);
	const [taxonList, setTaxonList] = React.useState<string[]>([]);
	const [importTime, setImportTime] = React.useState<number>(0);

	const [help, setHelp] = React.useState(true);
	const handleChangeHelp = (event: React.ChangeEvent<HTMLInputElement>) => {
		setHelp(event.target.checked);
	};

	const [activeStep, setActiveStep] = React.useState(0);
	const handleNext = () => {
		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};
	const handleReset = () => {
		setActiveStep(0);
		window.location.reload();
	};

	const { isFinished: isFinishedFile, isLoading: isLoadingFile } = StoreFile();
	const { isFinished: isFinishedRef } = StoreRef();

	// Error handling : affichage des messages d'erreurs
	const [errorMessage, setErrorMessage] = React.useState('');
	const { errorFile } = StoreFile();
	const { errorStatus } = StoreStatus();
	useEffect(() => {
		if (errorFile) {
			if (errorFile === "Request aborted") {
				return
			}
			setErrorMessage(errorFile);
			console.log("erreur sur le fichier : ", errorFile);
		}
		if (errorStatus) {
			setErrorMessage(errorStatus);
			console.log("erreur sur les statuts : ", errorStatus);
		}
	}, [errorFile, errorStatus]);

	const [open, setOpen] = React.useState(false);
	const handleClickOpen = () => {
	  setOpen(true);
	};
	const handleClose = () => {
	  setOpen(false);
	};

	function cancelUpload() {
		handleClose();
		handleReset();
		errorFile && StoreFile.setState({errorFile: ""});
		errorStatus && StoreStatus.setState({errorStatus: ""});
	};

	useEffect(() => {
		StoreFile.setState({isLoading: false});
	}, [isFinishedFile]);

	return (
		<Box sx={{ width: '100%' }}>

			<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
				<Tabs value={tab} onChange={handleChange}>
					<Tab 
						label={t("list-simple")}
						disabled={activeStep >= 1}
						{...TabIndex(0)}
					/>
					<Tab 
						label={t("list-statutes")}
						disabled={activeStep >= 1}
						{...TabIndex(1)}
					/>
				</Tabs>
			</Box>

			<CustomTabPanel value={tab} index={0}>
				<Stepper sx={{ mt: 4, mb: 1 }} activeStep={activeStep} alternativeLabel>
				{steps.map((label) => {
					return (
					<Step key={label}>
						<StepLabel>{label}</StepLabel>
					</Step>
					);
				})}
				</Stepper>
			</CustomTabPanel>

			<CustomTabPanel value={tab} index={1}>
				<Stepper sx={{ mt: 4, mb: 1 }} activeStep={activeStep} alternativeLabel>
					{stepsStatus.map((label) => {
						return (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
						);
					})}
				</Stepper>
			</CustomTabPanel>
			<FormControlLabel control={<Switch defaultChecked onChange={handleChangeHelp}/>} label={t("helper-title")}/>
			{help && (
				<Typography component={'span'} variant={'body2'}>
					<p>
					{t("helper-line-1")}
					<br/>{t("helper-line-2")}
					<br/>{t("helper-line-3")}
					<br/>{t("helper-line-4")}
					<Icon sx={{ mr: 0.5 }}>
						<SearchIcon fontSize="small" sx={{ mb: 1 }} />
					</Icon>
					<br/>{t("helper-line-5")}
					</p>
				</Typography>
			)}

			{file && (
			<>
				{errorMessage !== "" && activeStep > 0 && (
						<Box sx={{
							display:"flex",
							justifyContent:" center",
							minWidth: 300
						}}>
							<Typography 
								sx={{ mt: 2, mb: 1, color: "red" }}
							>
								{t("error-text")} {errorMessage}
							</Typography>
						</Box>
				)}
				<Box sx={{
					display:"flex",
					justifyContent:" center",
					minWidth: 300
				}}>
					<Button
						color='primary'
						onClick={handleClickOpen}
						variant='contained'
						sx={{ m: 1 }}
					>
						{t('reset-button')}
					</Button>
					<Dialog
						open={open}
						onClose={handleClose}
						aria-labelledby="alert-dialog-title"
						aria-describedby="alert-dialog-description"
					>
						<DialogTitle id="alert-dialog-title">
							⚠️ {t("warning-title")} ⚠️
						</DialogTitle>
						<DialogContent>
						<DialogContentText id="alert-dialog-description">
							{t("warning-text-1")}<br/>
							{t("warning-text-2")}
						</DialogContentText>
						</DialogContent>
						<DialogActions>
						<Button onClick={handleClose}>{t('no-text')}</Button>
						<Button onClick={cancelUpload} autoFocus>
							{t('yes-text')}
						</Button>
						</DialogActions>
					</Dialog>
				</Box>
			</>
			)}

			{activeStep == 0 && (
				<>

					<CustomTabPanel value={tab} index={0}>
						<Importer setFile={setFile} setImportTime={setImportTime} />
					</CustomTabPanel>

					<CustomTabPanel value={tab} index={1}>
						<Grid container spacing={2} columns={16}>
							<Grid item xs={8}>
								<Importer setFile={setFile} setImportTime={setImportTime} />
							</Grid>
							<Grid item xs={8} alignItems={'center'}>
								<UnitSelection setTerrUnit={setTerrUnit} />
							</Grid>
						</Grid>
					</CustomTabPanel>

					<SelectList setReferentiel={setReferentiel} setRefVersion={setRefVersion} setFilterType={setFilterType} />

					<Box sx={{
						display:"flex",
						justifyContent:" center",
						minWidth: 300
					}}>
						{isFinishedFile === true && isFinishedRef === true && file && !isLoadingFile && (
							<UploadFile file={file} referentiel={referentiel} terrUnit={terrUnit} tab={tab} setActiveStep={setActiveStep} setFileData={setfileData} importTime={importTime} />
						)}
						{isLoadingFile === true && (
							<LoadingButton
								loading={true}
								startIcon={<SaveIcon />}
								loadingPosition="start"
								variant="contained"
								color="secondary"
								sx={{ mr: 1 }}
							>
								{t("import-loading")}
							</LoadingButton>
						)}
					</Box>
				</>
			)}

			{activeStep >= 1 && (
				<>
					<Typography variant='h6' sx={{ mb: 2, mt: 2}}>
						{activeStep >= 2 && (
							<Icon color="primary" sx={{ mr: 0.5}}>
								<CheckCircleIcon sx={{ m: 0.1 }}/>
							</Icon>
						)}
					</Typography>
					{activeStep == 1 && (
						<>
							<MatchingTable fileData={fileData} setTaxonList={setTaxonList} handleNext={handleNext} tab={tab} setfileData={setfileData} referentiel={referentiel} refVersion={refVersion} filterType={filterType} errorMessage={errorMessage} />
						</>
					)}
				</>
			)}

			{activeStep >= 2 && tab == 1 && (
				<>
					<Typography variant='h6' sx={{ mt: 2, mb: 1 }}>
						{t("statutes-title")}
					</Typography>
					{activeStep == 2 && (
						<>
							<StatusTable taxonList={taxonList} />
						</>
					)}
				</>
			)}
		</Box>
	);
}